import React from 'react';
import 'fragments/announcements/announcements-list.scss';
import { Empty, Spin } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { useTranslator } from 'tools/view-hooks/translator-hook';
import { useAnnouncementsListController } from 'fragments/announcements/announcements-list.controller';
import { Announcement } from 'services/announcements/announcements.service';

const BODY_PREVIEW_LENGTH = 120;

export const AnnouncementsListFragment: React.FC = () => {
    const { translate } = useTranslator();
    const [searchParams] = useSearchParams();
    const siteName = searchParams.get('site_name') || '';
    const { state, events } = useAnnouncementsListController();

    const preview = (body: string) =>
        body.length > BODY_PREVIEW_LENGTH ? `${body.slice(0, BODY_PREVIEW_LENGTH)}…` : body;

    const renderCard = (item: Announcement) => (
        <button
            key={item.id}
            type={'button'}
            className={`announcements-card${item.read ? '' : ' announcements-card--unread'}`}
            onClick={() => events.onOpen(item.id)}
        >
            <span className={'announcements-card-meta'}>
                {!item.read && <span className={'announcements-card-dot'} aria-hidden />}
                {translate({ key: 'announcements.source' })} · {dayjs(item.publishedAt).format('DD/MM/YY HH:mm')}
            </span>
            <span className={'announcements-card-title'}>{item.title}</span>
            <span className={'announcements-card-preview'}>{preview(item.body)}</span>
        </button>
    );

    return (
        <div className={'announcements-list'}>
            <div className={'announcements-header'}>
                <h1 className={'announcements-header-title'}>{translate({ key: 'announcements.title' })}</h1>
                {siteName && <span className={'announcements-header-sitename'}>{siteName}</span>}
            </div>

            {state.loading ? (
                <Spin className={'announcements-spin'} />
            ) : state.announcements.length === 0 ? (
                <Empty
                    className={'announcements-empty'}
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={
                        <>
                            <p className={'announcements-empty-title'}>
                                {translate({ key: 'announcements.empty-title' })}
                            </p>
                            <p className={'announcements-empty-subtitle'}>
                                {translate({ key: 'announcements.empty-subtitle' })}
                            </p>
                        </>
                    }
                />
            ) : (
                <div className={'announcements-cards'}>{state.announcements.map(renderCard)}</div>
            )}

            {state.isAdmin && (
                <button
                    type={'button'}
                    className={'announcements-fab'}
                    aria-label={translate({ key: 'announcements.compose-title' })}
                    onClick={events.onCompose}
                >
                    <PlusOutlined aria-hidden />
                </button>
            )}
        </div>
    );
};
